<template>
  <div class="chart-view">
    <header-view />
    <div class="header-toolbar">
      <div class="left">
        <template v-if="!elementFocused">
          <button @click="showOptions('dateOptions')" class="btn secondary sml">Filter Time Range</button>
          <button @click="showOptions('statusOptions')" class="btn secondary sml">Filter Status</button>
          <!-- <button @click="showOptions('schoolOptions')" class="btn secondary sml">Filter Schools</button> -->
          <button @click="showOptions('problemOptions')" class="btn secondary sml">Filter Problems</button>
        </template>
        <template v-else>
          <button @click="resetFocus" class="btn secondary sml">Restore All</button>
        </template>
      </div>
      <div class="right">
        <div class="field">
          Time:
          <span class="field">
            <label>
              <input type="radio" name="view" value="normalized" @change="toggleXMode" :checked="mode != 'real'">  
              <span>Scaled</span>
            </label>
          </span>
          <span class="field">
            <label>
              <input type="radio" name="view" value="real" @change="toggleXMode" :checked="mode === 'real'"> 
              <span>Real</span>
            </label>
          </span>
        </div>
        <template v-if="!elementFocused">
          <div class="field">
            <label>
              <input type="checkbox" name="linkStatus" value="linkStatus" @change="toggleLineMode" :checked="connectProbs">  
              Link status
            </label>
          </div>
          <div class="field">
            <label>
              <input type="checkbox" name="showHistory" value="showHistory" @change="toggleShowHistory" :checked="showHistory">  
              Show History
            </label>
          </div>
        </template>
        <!-- <button @click="toggleXMode" class="btn secondary sml">Toggle Time</button> -->
        <div class="toolbar">
          <div class="input">
            <span class="clear" v-if="schoolFinder !== ''" @click="schoolFinder = ''">&times;</span>
            <input type="text" v-model="schoolFinder" placeholder="Find a school">
          </div>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="chart">
        <svg id="superChart" />
        <div class="tooltip tip" @mouseenter="cancelTooltipHide">
          <new-problem-view v-if="highlitedProblem" :canedit="true" :problem="highlitedProblem"/>
        </div>
        <div class="rowTooltip tip"></div>
      </div>
      <div class="sidebar">
        <div class="status-options schools">
          <ul>
            <li v-for="school in filteredSchools" v-bind:key="school['value']" @mouseenter="highlightSchool($event, parseInt(school.value))" @mouseleave="removeHighlightSchool" :id="'schoolItem-' + school.value" :class="{'focused': (focusedSchools && focusedSchools.indexOf(parseInt(school.value)) > -1)}">
              <div class="schoolinfo">
                <span class="input"><input type="checkbox" name="" value="" @change.stop="focusSchool(parseInt(school.value))" :checked="(focusedSchools && focusedSchools.indexOf(parseInt(school.value)) > -1)"></span>
                <span class="details" @click="focusSchool(school.value)" >
                  <span class="info">{{ school.shortLabel }}</span>
                  <span class="count">{{ problemCountForSchool(parseInt(school.value)) }}</span>
                  <span class="count">{{ pctCompleteForSchool(parseInt(school.value)) }}</span>
                  <span class="count">{{ adtcForSchool(parseInt(school.value)) }}</span>
                </span>
              </div>
              <div v-if="focusedSchools && focusedSchools.indexOf(parseInt(school.value)) > -1" class="problemsList">
                <div v-for="problem in focusedSchoolsProblems(parseInt(school.value))" v-bind:key="problem.id" :class="{'item': true, 'hilited': (highlitedProblem && highlitedProblem.id === problem.id) }" @mouseenter="hiliteProb(problem)" @mouseleave="dehiliteProblem" @click.stop="editProblem(problem)">
                  <div class="status">
                    <span :class="'status-flag prob-bg-' + problem.problemType.value ">&nbsp;</span>
                    {{ composeDate(problem.status.lastModified) }} -
                      <span v-if="problem.floorType.value">{{ problem.floorType.shortLabel }} </span>
                      <span v-if="problem.roomType.value">{{ problem.roomType.shortLabel }} </span>
                      <span v-if="problem.roomName">{{ problem.roomName }} </span>
                  </div>
                  <div class="count" v-if="dtcForProblem(problem)">
                    {{ dtcForProblem(problem) }}
                  </div>
                  <div class="count" v-else>
                    <i>{{ dtluForProblem(problem) }}</i>
                  </div>
                </div>   
              </div>
            </li>
          </ul>
        </div>
        <div class="schoolTotals">
            <span class="count"><b>{{ filteredCalcs.problemCount }}</b> problems in <b>{{ filteredCalcs.schoolCount }}</b> schools</span>
        </div>
        <div class="schoolTotals">
            <span class="count"><b>{{ filteredCalcs.completedProblemCount }}</b> problems (<b>{{ filteredCalcs.completedProblemPct }}</b>) completed in <b>{{ filteredCalcs.schoolsWithCompletedCount }}</b> schools averaging <b>{{ filteredCalcs.averageDaysToCompleted }}</b> days to complete</span>
        </div>
        <div class="schoolTotals">
            <span class="count"><b>{{ filteredCalcs.uncompletedProblemCount }}</b> problems (<b>{{ filteredCalcs.uncompletedProblemPct }}</b>) in progress in <b>{{ filteredCalcs.schoolsWithUncompletedCount }}</b> schools averaging <b>{{ filteredCalcs.averageDaysSinceReported }}</b> days since being reported</span>
        </div>
      </div>
    </div>

    <div class="schoolTooltip tip"></div>
    <problem-editor-modal :create="create" :edit="edit" :profile="profile" :fulledit="['Admin','Reporter'].indexOf(profile.role) !== -1" :problem="problemToEdit" @cancel="problemEditorCanceled" @created="problemEditorCreated" @updated="problemEditorUpdated" />
    <modal name="dateOptions"
        width="400"
        height="auto"
        classes="v--modal datepickers">
      <div class="status-options">
        <div class="toolbar">
          <button class="btn-img med btn-cancel" @click="hideOptions('dateOptions')" title="Cancel"></button>
          <button @click="clearDates" class="btn secondary sml">Clear Dates</button>
        </div>
        <div class="dateRange">
          <div class="field">
            <label>Start Date</label>
            <date-pick
              :inputAttributes="{placeholder: 'Pick Date', size: 15}"
              v-model="timeRangeStart"
              :format="dateFormat"
              :parseDate="parseDate"
              :isDateDisabled="isFutureStartDate"
              :formatDate="formatDate"
              @input="rangeDateChanged"
            ></date-pick>
          </div>
          <div class="field">
            <label>End Date</label>
            <date-pick
              :inputAttributes="{placeholder: 'Pick Date', size: 15}"
              v-model="timeRangeEnd"
              :format="dateFormat"
              :parseDate="parseDate"
              :isDateDisabled="isFutureDate"
              :formatDate="formatDate"
              @input="rangeDateChanged"
            ></date-pick>
          </div>
        </div>
      </div>
    </modal>
    <modal name="statusOptions"
        width="400"
        height="auto">
      <div class="status-options">
        <div class="toolbar">
          <button class="btn-img med btn-cancel" @click="hideOptions('statusOptions')" title="Cancel"></button>
          <button @click="selectAllStatus" class="btn secondary sml">Select All</button>
          <button @click="clearAllStatus" class="btn secondary sml">Clear All</button>
        </div>
        <ul>
          <li v-for="(status, index) in flippedStatus" v-bind:key="status['value']" @click="toggleStatusIndex(index)">
            <div :class="'status-' + status.value "><span v-show="selectedStatusIndexes[statuses.length - (index + 1)] == true" class="check">&#10004;</span>{{ status.shortLabel }}</div>
          </li>
        </ul>
      </div>
    </modal>
    <modal name="problemOptions"
        width="400"
        height="auto">
      <div class="status-options">
        <div class="toolbar">
          <button class="btn-img med btn-cancel" @click="hideOptions('problemOptions')" title="Cancel"></button>
          <button @click="selectAllTypes" class="btn secondary sml">Select All</button>
          <button @click="clearAllTypes" class="btn secondary sml">Clear All</button>
        </div>
        <ul>
          <li v-for="type in types" v-bind:key="type['value']" @click="toggleTypeHidden(type.value)">
            <div :class="'prob-bg-' + type.value "><span v-show="typesHidden.indexOf(parseInt(type.value)) === -1" class="check">&#10004;</span>{{ type.shortLabel }}</div>
          </li>
        </ul>
      </div>
    </modal>
    
  </div>
</template>

<script>
  import Vue from 'vue'
  import database from '@/api/database.js'
  import ProblemEditorModal from '@/components/ProblemEditorModal.vue'
  import NewProblemView from '@/components/NewProblemView.vue'
  import HeaderView from '@/components/HeaderView.vue'
  import * as d3 from 'd3'
  import DatePick from 'vue-date-pick'
  import { isSameDay, format, parse, differenceInDays } from 'date-fns'

  export default {
    name: 'Chart',
    components: {
      HeaderView, ProblemEditorModal, DatePick, NewProblemView
    },
    data() {
      return {
        problems: null,
        dataLoaded: false,
        margins: {
          top: 10,
          right: 20,
          bottom: 70,
          left: 140
        },
        style: {
          radius: 2.5,
          selectedRadius: 10,
          tickCount: 20
        },
        typesHidden: [], 
        types: [
          {
            'labels': {
              'locale': 'en',
              'long': 'Flaking Paint',
              'medium': 'Flaking Paint',
              'short': 'Flaking Paint'
            },
            'sequence': 1,
            'value': '12',
            'longLabel': 'Flaking Paint',
            'mediumLabel': 'Flaking Paint',
            'shortLabel': 'Flaking Paint'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Physical Damage',
              'medium': 'Physical Damage',
              'short': 'Physical Damage'
            },
            'sequence': 2,
            'value': '15',
            'longLabel': 'Physical Damage',
            'mediumLabel': 'Physical Damage',
            'shortLabel': 'Physical Damage'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Water Stains / Damage / Dampness',
              'medium': 'Water Stains / Damage / Dampness',
              'short': 'Water Damage'
            },
            'sequence': 3,
            'value': '13',
            'longLabel': 'Water Stains / Damage / Dampness',
            'mediumLabel': 'Water Stains / Damage / Dampness',
            'shortLabel': 'Water Damage'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Active Water &/or Steam Leaks',
              'medium': 'Active Water &/or Steam Leaks',
              'short': 'Water / Steam Leaks'
            },
            'sequence': 4,
            'value': '18',
            'longLabel': 'Active Water &/or Steam Leaks',
            'mediumLabel': 'Active Water &/or Steam Leaks',
            'shortLabel': 'Water / Steam Leaks'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Mold Growth',
              'medium': 'Mold Growth',
              'short': 'Mold Growth'
            },
            'sequence': 5,
            'value': '14',
            'longLabel': 'Mold Growth',
            'mediumLabel': 'Mold Growth',
            'shortLabel': 'Mold Growth'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Rodent / Insect / Pest Sightings',
              'medium': 'Rodent / Insect / Pest Sightings',
              'short': 'Pest Sightings'
            },
            'sequence': 6,
            'value': '16',
            'longLabel': 'Rodent / Insect / Pest Sightings',
            'mediumLabel': 'Rodent / Insect / Pest Sightings',
            'shortLabel': 'Pest Sightings'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Rodent Droppings',
              'medium': 'Rodent Droppings',
              'short': 'Rodent Droppings'
            },
            'sequence': 7,
            'value': '17',
            'longLabel': 'Rodent Droppings',
            'mediumLabel': 'Rodent Droppings',
            'shortLabel': 'Rodent Droppings'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Thermal Control Problems',
              'medium': 'Thermal Control Problems',
              'short': 'Thermal Control'
            },
            'sequence': 8,
            'value': '19',
            'longLabel': 'Thermal Control Problems',
            'mediumLabel': 'Thermal Control Problems',
            'shortLabel': 'Thermal Control'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Working Condition Related Illness/Symptoms',
              'medium': 'Working Condition Related Illness/Symptoms',
              'short': 'Workplace Illness'
            },
            'sequence': 9,
            'value': '21',
            'longLabel': 'Working Condition Related Illness/Symptoms',
            'mediumLabel': 'Working Condition Related Illness/Symptoms',
            'shortLabel': 'Workplace Illness'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'General Indoor Air Quality Issues',
              'medium': 'General Indoor Air Quality Issues',
              'short': 'Indoor Air Quality'
            },
            'sequence': 10,
            'value': '20',
            'longLabel': 'General Indoor Air Quality Issues',
            'mediumLabel': 'General Indoor Air Quality Issues',
            'shortLabel': 'Indoor Air Quality'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Safety Hazards',
              'medium': 'Safety Hazards',
              'short': 'Safety Hazards'
            },
            'sequence': 11,
            'value': '22',
            'longLabel': 'Safety Hazards',
            'mediumLabel': 'Safety Hazards',
            'shortLabel': 'Safety Hazards'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Generally Dirty Conditions',
              'medium': 'Generally Dirty Conditions',
              'short': 'Dirty Conditions'
            },
            'sequence': 12,
            'value': '23',
            'longLabel': 'Generally Dirty Conditions',
            'mediumLabel': 'Generally Dirty Conditions',
            'shortLabel': 'Dirty Conditions'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Other',
              'medium': 'Other',
              'short': 'Other'
            },
            'sequence': 13,
            'value': '24',
            'longLabel': 'Other',
            'mediumLabel': 'Other',
            'shortLabel': 'Other'
          }
        ],
        selectedStatusIndexes: [],
        statuses: [
          // {
          //   'labels': {
          //     'locale': 'en',
          //     'long': 'Archived',
          //     'medium': 'Archived',
          //     'short': 'Archived'
          //   },
          //   'sequence': 17,
          //   'value': '-1',
          //   'longLabel': 'Archived',
          //   'mediumLabel': 'Archived',
          //   'shortLabel': 'Archived'
          // },
          {
            'labels': {
              'locale': 'en',
              'long': 'Problem Reported',
              'medium': 'Problem Reported',
              'short': 'Problem Reported'
            },
            'sequence': 0,
            'value': '0',
            'longLabel': 'Problem Reported',
            'mediumLabel': 'Problem Reported',
            'shortLabel': 'Problem Reported'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Need More Info',
              'medium': 'Need More Info',
              'short': 'Need More Info'
            },
            'sequence': 1,
            'value': '1',
            'longLabel': 'Need More Info',
            'mediumLabel': 'Need More Info',
            'shortLabel': 'Need More Info'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Problem Accepted',
              'medium': 'Problem Accepted',
              'short': 'Problem Accepted'
            },
            'sequence': 2,
            'value': '2',
            'longLabel': 'Problem Accepted',
            'mediumLabel': 'Problem Accepted',
            'shortLabel': 'Problem Accepted'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Additional Info Required To Repair',
              'medium': 'Additional Info Required To Repair',
              'short': 'Add. Info Req To Repair'
            },
            'sequence': 3,
            'value': '10',
            'longLabel': 'Additional Info Required To Repair',
            'mediumLabel': 'Additional Info Required To Repair',
            'shortLabel': 'Additional Info Required To Repair'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Under Investigation',
              'medium': 'Under Investigation',
              'short': 'Under Investigation'
            },
            'sequence': 4,
            'value': '3',
            'longLabel': 'Under Investigation',
            'mediumLabel': 'Under Investigation',
            'shortLabel': 'Under Investigation'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Work Order Created',
              'medium': 'Work Order Created',
              'short': 'Work Order Created'
            },
            'sequence': 5,
            'value': '14',
            'longLabel': 'Work Order Created',
            'mediumLabel': 'Work Order Created',
            'shortLabel': 'Work Order Created'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'To Be Done as Part of a Capital Project',
              'medium': 'Part of Capital Project',
              'short': 'Capital Project'
            },
            'sequence': 6,
            'value': '11',
            'longLabel': 'To Be Done as Part of a Capital Project',
            'mediumLabel': 'Part of Capital Project',
            'shortLabel': 'Capital Project'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'OEMS Notified',
              'medium': 'OEMS Notified',
              'short': 'OEMS Notified'
            },
            'sequence': 7,
            'value': '12',
            'longLabel': 'OEMS Notified',
            'mediumLabel': 'OEMS Notified',
            'shortLabel': 'OEMS Notified'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Site Visit Scheduled',
              'medium': 'Site Visit Scheduled',
              'short': 'Site Visit Scheduled'
            },
            'sequence': 8,
            'value': '4',
            'longLabel': 'Site Visit Scheduled',
            'mediumLabel': 'Site Visit Scheduled',
            'shortLabel': 'Site Visit Scheduled'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Evaluation Completed',
              'medium': 'Evaluation Completed',
              'short': 'Evaluation Completed'
            },
            'sequence': 9,
            'value': '5',
            'longLabel': 'Evaluation Completed',
            'mediumLabel': 'Evaluation Completed',
            'shortLabel': 'Evaluation Completed'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Work Scheduled',
              'medium': 'Work Scheduled',
              'short': 'Work Scheduled'
            },
            'sequence': 10,
            'value': '6',
            'longLabel': 'Work Scheduled',
            'mediumLabel': 'Work Scheduled',
            'shortLabel': 'Work Scheduled'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Work Started',
              'medium': 'Work Started',
              'short': 'Work Started'
            },
            'sequence': 11,
            'value': '7',
            'longLabel': 'Work Started',
            'mediumLabel': 'Work Started',
            'shortLabel': 'Work Started'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Work Order Closed',
              'medium': 'Work Order Closed',
              'short': 'Work Order Closed'
            },
            'sequence': 12,
            'value': '13',
            'longLabel': 'Work Order Closed',
            'mediumLabel': 'Work Order Closed',
            'shortLabel': 'Work Order Closed'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Work Completed',
              'medium': 'Work Completed',
              'short': 'Work Completed'
            },
            'sequence': 13,
            'value': '8',
            'longLabel': 'Work Completed',
            'mediumLabel': 'Work Completed',
            'shortLabel': 'Work Completed'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Still A Problem',
              'medium': 'Still A Problem',
              'short': 'Still A Problem'
            },
            'sequence': 14,
            'value': '15',
            'longLabel': 'Still A Problem',
            'mediumLabel': 'Still A Problem',
            'shortLabel': 'Still A Problem'
          },
          {
            'labels': {
              'locale': 'en',
              'long': 'Problem Resolved',
              'medium': 'Problem Resolved',
              'short': 'Problem Resolved'
            },
            'sequence': 16,
            'value': '9',
            'longLabel': 'Problem Resolved',
            'mediumLabel': 'Problem Resolved',
            'shortLabel': 'Problem Resolved'
          }
        ],
        schools: [],
        statusDates: [],
        schoolFinder: '',
        viz: {
          center: null,
          width: null,
          height: null
        },
        mode: 'real',
        problemToEdit: undefined,
        problemSelected: undefined,
        highlitedProblem: null,
        edit: false,
        create: false,
        profile: {
          role: 'tbd'
        },
        timeRangeStart: '',
        timeRangeEnd: '',
        dateFormat: 'MMM D YYYY',
        hideTimeout: null,
        connectProbs: false,
        showHistory: true,
        elementFocused: null,
        focusedStatus: null,
        focusedSchools: null
      }
    },
    mounted () {
      database.EventBus.$on('problem-added-remotely', this.problemAddedRemotely)
      database.EventBus.$on('problem-modified-remotely', this.problemModifiedRemotely)
      database.EventBus.$on('problem-removed-remotely', this.problemRemovedRemotely)

      this.resizeChart()
      database.getCurrentUserAndProfile().then(userAndProfile => {
        this.profile = userAndProfile.profile
      })
      database.getProblems().then(problems => {
        this.problems = problems.filter(p => p.status.value > -1)
        const schools = []
        const map = new Map()
        for (const problem of this.problems) {
          if(!map.has(parseInt(problem.school.value))){
              map.set(parseInt(problem.school.value), true);
              schools.push(problem.school)
          }
        }
        this.schools = schools.sort((a, b) => {
          let comparison = 0
          if (a.shortLabel > b.shortLabel) {
            comparison = 1
          } else if (a.shortLabel < b.shortLabel) {
            comparison = -1
          }
          return comparison
        })
        this.selectedStatusIndexes = this.statuses.map((s, i) => true)
        this.statusDates = this.filteredProblems.map(p => p.status.lastModified)
        this.dataLoaded = true
        this.initChart()
      }).catch(NotFound => {
        console.log(NotFound)
      })
    },
    created () {
      window.addEventListener('resize', this.resizeChart)
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.resizeChart)
    },
    computed: {
      filteredCalcs () {
        if (!this.filteredProblems) {
          return {
            problemCount: '',
            schoolCount: '',
            completedProblemCount: '',
            completedProblemPct: '',
            uncompletedProblemCount: '',
            uncompletedProblemPct: '',
            schoolsWithCompletedCount: '',
            schoolsWithoutCompletedCount: '',
            averageDaysSinceReported: '',
            averageDaysToCompleted: ''
          }
        }
        let problems = this.filteredProblems
        let schoolValues = this.filteredSchools ? this.filteredSchools.map(s => parseInt(s.value)) : []
        if (this.focusedSchools && this.focusedSchools.length > 1) {
          problems = this.filteredProblems.filter(p => this.focusedSchools.indexOf(parseInt(p.school.value)) > -1)
          schoolValues = this.focusedSchools
        }
        const completedStatusValues = [8,9]
        const completedProblems = problems.filter(p => completedStatusValues.indexOf(parseInt(p.status.value)) > -1)
        const uncompletedProblems = problems.filter(p => completedStatusValues.indexOf(parseInt(p.status.value)) === -1)
        const withComplete = new Map()
        const withUncomplete = new Map()
        for (const problem of problems) {
          const schoolValue = parseInt(problem.school.value)
          const statusValue = parseInt(problem.status.value)
          if(!withComplete.has(schoolValue)){
            if (completedStatusValues.indexOf(statusValue) > -1) {
              withComplete.set(schoolValue, true);
            }
          }
          if(!withUncomplete.has(schoolValue)){
            if (completedStatusValues.indexOf(statusValue) === -1) {
              withUncomplete.set(schoolValue, true);
            }
          }
        }
        const now = new Date().getTime()
        const daysSinceReported = uncompletedProblems.map(p => differenceInDays(now, p.created))
        const daysToCompleted = completedProblems.map(p => differenceInDays(p.status.lastModified, p.created))

        return {
          problemCount: problems.length,
          schoolCount: schoolValues.length,
          completedProblemCount: completedProblems.length,
          completedProblemPct: problems.length > 0 ? (completedProblems.length / problems.length * 100).toFixed(1) + '%' : '%',
          uncompletedProblemCount: uncompletedProblems.length,
          uncompletedProblemPct: problems.length > 0 ? (uncompletedProblems.length / problems.length * 100).toFixed(1) + '%' : '%',
          schoolsWithCompletedCount: withComplete.size,
          schoolsWithoutCompletedCount: withUncomplete.size,
          averageDaysSinceReported: daysSinceReported.length > 0 ? Math.round(daysSinceReported.reduce((p, c) => p + c, 0) / daysSinceReported.length) : 'N/A',
          averageDaysToCompleted: daysToCompleted.length > 0 ? Math.round(daysToCompleted.reduce((p, c) => p + c, 0) / daysToCompleted.length) : 'N/A'
        }
      },
      filteredSchools () {
        let schools = []
        const map = new Map()
        for (const problem of this.filteredProblems) {
          if(!map.has(parseInt(problem.school.value))){
              map.set(parseInt(problem.school.value), true);
              schools.push(problem.school)
          }
        }
        schools = schools.sort((a, b) => {
          let comparison = 0
          if (a.shortLabel > b.shortLabel) {
            comparison = 1
          } else if (a.shortLabel < b.shortLabel) {
            comparison = -1
          }
          return comparison
        })
        if (this.schoolFinder !== '') {
          schools = schools.filter(s => s.shortLabel.toLowerCase().includes(this.schoolFinder.toLowerCase()))
        }
        if (this.focusedSchools) {
          schools.filter(s => this.focusedSchools.indexOf(parseInt(s.value)) > -1)
        } else if (this.schoolFinder !== '') {
          schools.filter(s => s.shortLabel.toLowerCase().includes(this.schoolFinder.toLowerCase()))
        }
        return schools
      },
      filteredDates () {
        let dates = this.filteredProblems.map(p => p.status.lastModified)
        // if (this.mode === 'real') {
          this.filteredProblems.forEach(p => {
            p.statusHistory.forEach(status => {
              const statusIndex = this.statuses.findIndex(s => s.shortLabel === status.shortLabel)
              if (this.selectedStatusIndexes[statusIndex]) {
                dates.push(status.lastModified)
              }
            })
          })
        // }
        return dates
      },
      flippedStatus () {
        return [].concat(this.statuses).reverse()
      },
      filteredProblems() {
        if (!this.problems) { return [] }
        let filtered = this.problems.filter(p => p.status.value > 0)
        filtered = this.problems.filter(p => {
          if (this.focusedStatus) {
            return this.focusedStatus.shortLabel === p.status.shortLabel
          } else {
            const statusIndex = this.statuses.findIndex(s => s.shortLabel === p.status.shortLabel)
            return this.selectedStatusIndexes[statusIndex]
          }
        })
        if (this.typesHidden.length > 0) {
          filtered = filtered.filter(p => {
            return this.typesHidden.indexOf(parseInt(p.problemType.value)) === -1
          })
        }
        if (this.timeRangeStart !== '' && this.timeRangeEnd !== '') {
          filtered = filtered.filter(p => {
            return p.status.lastModified > parse(this.timeRangeStart).getTime() && p.status.lastModified < parse(this.timeRangeEnd).getTime()
          })
        }
        return filtered.sort((a, b) => {
          return a.status.lastModified - b.status.lastModified
        })
      },
      filteredStatuses() {
        if (!this.problems) { return [] }
        if (this.focusedStatus) {
          return [this.focusedStatus]
        } else {
          const filtered = this.statuses.filter((s, i) => {
            return this.selectedStatusIndexes[i]
          })
          return filtered
        }
      }
    },
    methods: {
      clearGraph () {
        const svg = d3.select('#superChart')
        svg.selectAll('*').remove()
      },
      resizeChart () {
        if (this.resizeTimer) {
          window.clearTimeout(this.resizeTimer)
        }
        this.resizeTimer = setTimeout(this.doTheResize, 150)
      },
      doTheResize () {
        this.clearGraph()
        const width = parseInt(d3.select('.chart').style('width'))
        const screenHeight = parseInt(window.innerHeight || document.documentElement.clientHeight || document.body.clientHeigh)
        let svg = d3.select('#superChart')
        svg
          .attr('width', width)
          .attr('height', screenHeight - 95)
        d3.selectAll('.container,.sidebar').attr('style', 'height:' + (screenHeight - 95) + 'px;')
        this.viz.center = {x: Math.round(parseInt(svg.attr('width')) / 2), y: Math.round(svg.attr('height')) / 2}
        this.viz.width = parseInt(svg.attr('width'))
        this.viz.height = parseInt(svg.attr('height'))
        if (this.dataLoaded) {
          this.initChart()
        }
      },
      initChart () {
        let svg = d3.select('#superChart')
        svg.empty()
        this.drawRows()
        if (this.problems) {
          this.drawProblems()
          this.drawTimes()
        }
      },
      drawRows () {
        if (!this.viz.height) {
          return
        }
        const yRowHeight = (this.viz.height - this.margins.bottom - this.margins.top) / this.filteredStatuses.length
        
        let svg = d3.select('#superChart')
        let rowGroup = svg.insert('g').lower()
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('class', 'rows')

        let lineGenerator = d3.line()
        this.filteredStatuses.forEach((status, index) => {
          const y = this.viz.height - this.margins.bottom - yRowHeight - (yRowHeight * index)
          rowGroup.append('rect')
            .attr('x', this.margins.left)
            .attr('y', y)
            .attr('width', this.viz.width - this.margins.left - this.margins.right)
            .attr('height', yRowHeight - 1)
            .attr('class', 'status-' + status.value)
            .attr('opacity', 0.1)
            .on('click', this.resetMode)

          rowGroup.append('rect')
            .datum(status)
            .attr('x', 20)
            .attr('y', y)
            .attr('width', this.margins.left - 20)
            .attr('height', yRowHeight - 1)
            .attr('class', 'statusfield status-' + status.value)
            .attr('opacity', 0.1)
            .on('click', (s) => {
              if (this.focusedStatus && this.focusedStatus.value === s.value) {
                this.resetFocus()
              } else {
                this.focusStausRow(s)
              }
            })
            .on('mouseover', (d, i, n) => {
              this.showStatusTip(d, y, yRowHeight)
            })
            .on('mouseout', (d, i, n) => {
              this.hideStatusTip()
            })
          
          rowGroup.append('path')
            .attr('d', lineGenerator([
              [20, y + yRowHeight],
              [this.viz.width - this.margins.right, y + yRowHeight]
            ]))
            .attr('class', 'status-' + status.value)
            .attr('opacity', 0.5)
          
          rowGroup.append('path')
            .attr('d', lineGenerator([
              [95, y],
              [95, y + yRowHeight]
            ]))
            .attr('class', 'status-' + status.value)
            .attr('opacity', 0.5)

          rowGroup.append('text')
            .attr('class', 'label')
            .attr('pointer-events', 'none')
            .attr('width', 40)
            .attr('x', 118)
            .attr('y', () => {
              return this.viz.height - this.margins.bottom - (yRowHeight * 0.5) - (yRowHeight * index) + 5
            })
            .style('display', () => { return 'inline' })
            .attr('text-anchor',  'middle')
            .text(this.filteredProblems.filter(p => parseInt(p.status.value) === parseInt(status.value)).length)
      
          rowGroup.append('foreignObject')
            .attr('class', 'label')
            .attr('pointer-events', 'none')
            .attr('width', 60)
            .attr('height', yRowHeight)
            .attr('x', 25)
            .attr('y', y)
            .style('display', () => { return 'inline' })
            .attr('text-anchor',  'end')
            .append('xhtml:body')
              .html('<div class="label_el">' + status.labels.short + '</div>')
        })

        rowGroup.append('text')
          .attr('class', 'label')
          .attr('pointer-events', 'none')
          .attr('width', 100)
          .attr('x', 85)
          .attr('y', () => {
            return this.viz.height - this.margins.bottom + 20
          })
          .style('display', () => { return 'inline' })
          .attr('text-anchor',  'end')
          .text('Total Problems')

        rowGroup.append('text')
          .attr('class', 'label')
          .attr('pointer-events', 'none')
          .attr('width', 40)
          .attr('x', 118)
          .attr('y', () => {
            return this.viz.height - this.margins.bottom + 20
          })
          .style('display', () => { return 'inline' })
          .attr('text-anchor',  'middle')
          .text((this.filteredProblems.length))
      },
      focusSchool (schoolValue) {
        if (this.focusedSchools && this.focusedSchools.indexOf(parseInt(schoolValue)) > -1) {
          this.unfocusSchool(parseInt(schoolValue))
          return
        }
        this.elementFocused = true
        const problems = this.filteredProblems.filter(p => parseInt(p.school.value) === parseInt(schoolValue))
        d3.select('.problems').attr('opacity', 0.1)
        let svg = d3.select('#superChart')
        let problemGroup = svg.insert('g')
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('id', 'focusSchool-' + schoolValue)
          .attr('class', 'focusSchool').raise()
        this.drawProblems(problemGroup, problems, this.connectProbs)
        if (this.focusedSchools === null) {
          this.focusedSchools =  []
        }
        this.focusedSchools.push(parseInt(schoolValue))
      },
      unfocusSchool (schoolValue) { 
        this.focusedSchools = this.focusedSchools.filter(s => parseInt(s) !== parseInt(schoolValue))
        this.elementFocused = this.focusedStatus != null && this.focusedSchools.length === 0
        d3.selectAll('#focusSchool-' + schoolValue).remove()
        if (this.focusedSchools.length === 0) {
          this.focusedSchools = null
          this.redraw()
        }
      },
      focusStausRow (status) {
        this.elementFocused = true
        this.focusedStatus = status
        this.redraw()
      },
      resetFocus () {
        this.elementFocused = null
        this.focusedStatus = null
        if (this.focusedSchools) {
          this.focusedSchools.map(schoolValue => this.unfocusSchool(schoolValue))
        }
        this.focusedSchools = null
        this.removeHighlightSchool()
        this.redraw()
      },
      showStatusTip(status, y, yRowHeight) {
        const probs = this.filteredProblems.filter(p => p.status.shortLabel === status.shortLabel)
        let popContent = '<table class="c3-tooltip"><tbody>'
        this.types.forEach(t => {
          const count = probs.filter(p => p.problemType.shortLabel === t.shortLabel).length
          if (count > 0) {
            popContent += '<tr class="c3-tooltip-name"><td class="name"><span class="prob-bg-' + t.value + '"></span>' + t.shortLabel + '</td><td class="value">' + count + '</td></tr>'
          }
        })
        popContent += '<tr class="c3-tooltip-name"><td class="name" style="text-align:right;">Total</td><td class="value">' + probs.length + '</td></tr>'
        popContent += '</tbody></table>'
        const tip = d3.select('.rowTooltip').html(popContent)
        const tipWidth = tip.node().offsetWidth  
        const tipHeight = tip.node().offsetHeight
        const offset = 20
        tip
        .classed('right', true)
          .attr('style', 'top:' + (y + (yRowHeight / 2) - (tipHeight / 2)) + 'px; left:' + (this.margins.left + offset) + 'px;')
        this.highlightStatus(probs)
      },
      hideStatusTip () {
        d3.select('.rowTooltip').attr('style', '').classed('top right left shown', false)
        this.redraw()
      },
      highlightStatus (problems) {
        // don't perform the highlight if a school is in focus
        if (this.focusedSchools && this.focusedSchools.length > 0) {
          return
        }
        d3.selectAll('.highlitedSchool').remove()
        d3.select('.problems').attr('opacity', 0.1)
        let svg = d3.select('#superChart')
        let problemGroup = svg.insert('g')
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('class', 'highlitedSchool').raise()
        this.drawProblems(problemGroup, problems, this.connectProbs)
      },
      highlightSchool (event, school) {
        if (!this.focusedSchools) {
          d3.selectAll('.highlitedSchool').remove()
        }
        const problems = this.filteredProblems.filter(p => parseInt(p.school.value) === parseInt(school))
        d3.select('.problems').attr('opacity', 0.1)
        let svg = d3.select('#superChart')
        let problemGroup = svg.insert('g')
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('class', 'highlitedSchool').raise()
        this.drawProblems(problemGroup, problems, this.connectProbs)
        if (event && (this.focusedSchools && this.focusedSchools.indexOf(parseInt(school)) === -1 || !this.focusedSchools)) {
          this.showSchoolTip(problems, event)
        }
      },
      removeHighlightSchool () {
        this.hideShoolTip()
        d3.selectAll('.highlitedSchool').remove()

        if (this.focusedSchools === null || this.focusedSchools.length === 0) {
          d3.select('.problems').attr('opacity', 1)
        }
      },
      showSchoolTip(problems, event) {
        const targetRect = d3.select(event.target).node().getBoundingClientRect()
        const containerRect = d3.select('.chart-view').node().getBoundingClientRect()
        const screenHeight = parseInt(window.innerHeight || document.documentElement.clientHeight || document.body.clientHeigh)
        let popContent = '<table class="c3-tooltip"><tbody>'
        this.types.forEach(t => {
          const count = problems.filter(p => p.problemType.shortLabel === t.shortLabel).length
          if (count > 0) {
            popContent += '<tr class="c3-tooltip-name"><td class="name"><span class="prob-bg-' + t.value + '"></span>' + t.shortLabel + '</td><td class="value">' + count + '</td></tr>'
          }
        })
        popContent += '<tr class="c3-tooltip-name"><td class="name" style="text-align:right;">Total</td><td class="value">' + problems.length + '</td></tr>'
        popContent += '</tbody></table>'
        const tip = d3.select('.schoolTooltip').html(popContent)
        const tipWidth = tip.node().offsetWidth  
        const tipHeight = tip.node().offsetHeight
        const offset = 10
        const x = targetRect.x - containerRect.x
        let y = targetRect.y - containerRect.y + (targetRect.height / 2) - (tipHeight / 2)
        // if (y + tipHeight > screenHeight) {
        //   y = targetRect.y - containerRect.y - tipHeight - offset
        //   tip
        //   .classed('bottom', true)
        //     .attr('style', 'top:' + y + 'px; left:' + x + 'px;')
        //   return
        // }
        // const x = d3.select(targetLi).node().getBoundingClientRect()
        tip
        .classed('left', true)
          .attr('style', 'top:' + y + 'px; right:' + 320 + 'px; left: auto;')
      },
      hideShoolTip () {
        d3.select('.schoolTooltip').attr('style', '').classed('top right left bottom shown', false)
      },
      drawProblems (container, problems, connected) {
        const yRowHeight = (this.viz.height - this.margins.bottom - this.margins.top) / this.filteredStatuses.length
        let svg = d3.select('#superChart')
        let problemGroup = container || svg.insert('g')
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('class', 'problems')
        let problemLines = problemGroup.insert('g')
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('class', 'problems lines')

        const probsToDraw = problems || this.filteredProblems
          // xValues(prob.status.lastModified)
        const radiusMultiplier = 1 + ((this.statuses.length - this.filteredStatuses.length) * 0.1)
        const radius = this.style.radius * radiusMultiplier
        probsToDraw
        .forEach((prob, index) => {
          prob.normalizedIndex = this.filteredProblems.findIndex(p => p.id === prob.id)
          const statusVert = this.yForStatus(prob.status.shortLabel, yRowHeight) + this.yForType(prob.problemType.shortLabel, yRowHeight)
          const x = this.xForProblem(prob, prob.status)
          const y = this.viz.height - (this.margins.bottom + statusVert)
            // .attr('cx', x)
            // .attr('cy', y)
            // .attr('r', this.style.radius)
          problemGroup.append('rect')
            .datum(prob)
            .attr('class', 'prob prob-' + (prob.problemType.value | 0) + ' school-' + prob.school.value)
            .attr('id', 'prob-' + prob.id)
            .attr('x', x - radius)
            .attr('y', y - radius)
            .attr('width', radius * 2)
            .attr('height', radius * 2)
            .attr('stroke', 'white')
            .attr('stroke-width', '0.5')
            .on('click', (d) => { 
              if (this.focusedSchools && this.focusedSchools.indexOf(parseInt(d.school.value)) > -1) {
                return
              }

              this.focusSchool(d.school.value)
              const node = document.querySelector('#schoolItem-' + d.school.value) 
              document.querySelector('.container .status-options').scrollTop = node.offsetTop - node.offsetHeight
              // if (this.problemSelected === d.id) {
              //   this.editProblem(d)
              // } else {
              //   this.selectProb(d)
              // }
            })
            .on('mouseover', (d, i, n) => {
              this.highlitedProblem = d
              // d3.select(n[i]).attr('r', this.style.radius * 2)
              this.showProblemTooltip(parseFloat(d3.select(n[i]).attr('x')) + radius, parseFloat(d3.select(n[i]).attr('y')) + radius)
              this.connectProb(d)
            })
            .on('mouseout', (d, i, n) => {
              d3.select(n[i]).attr('r', this.style.radius)
              if (this.problemSelected === undefined || this.problemSelected !== d.id) {
                this.hideTooltip()
              }
              d3.selectAll('.tempProbsDos').remove()
            })
          if (!this.showHistory) {
            return
          }
          let lineGenerator = d3.line()
          var historyItems = prob.statusHistory.concat([prob.status])
          historyItems = historyItems.filter(s => {
            const found = this.filteredStatuses.findIndex(fs => fs.shortLabel === s.shortLabel)
            return found > -1
          })
          historyItems.sort((a, b) => {
            return a.lastModified - b.lastModified
          }).forEach((s, i) => {
            const statusVert = this.yForStatus(s.shortLabel, yRowHeight) + this.yForType(prob.problemType.shortLabel, yRowHeight)
            let sPoint = { 
              x: this.xForProblem(prob, s), 
              y: this.viz.height - (this.margins.bottom + statusVert)
            }
            if (s.lastModified !== prob.status.lastModified) {
              problemGroup.append('circle')
                .datum(prob)
                .attr('cx', sPoint.x)
                .attr('cy', sPoint.y)
                .attr('r', radius)
                .attr('class', 'tProb prob-' + (prob.problemType.value | 0) )
                .attr('opacity', 0.3)
                .on('click', (d) => { 
                  if (this.focusedSchools && this.focusedSchools.indexOf(parseInt(d.school.value)) > -1) {
                    return
                  }
                  this.focusSchool(d.school.value)
                })
                .on('mouseover', (d, i, n) => {
                  if (this.focusedSchools && this.focusedSchools.indexOf(parseInt(d.school.value)) > -1) {
                    return
                  }
                  this.highlitedProblem = d
                  this.showProblemTooltip(x, y)
                  this.connectProb(d)
                })
                .on('mouseout', (d, i, n) => {
                  if (this.problemSelected === undefined || this.problemSelected !== d.id) {
                      this.hideTooltip()
                  }
                  d3.selectAll('.tempProbsDos').remove()
                })
            }
            
            if (this.connectProbs || connected) {
              let prevPoint
              if (i > 0) {
                const prevStatus = historyItems[i - 1]
                prevPoint = {
                  x: this.xForProblem(prob, prevStatus), 
                  y: this.viz.height - (this.margins.bottom + this.yForStatus(prevStatus.shortLabel, yRowHeight) + this.yForType(prob.problemType.shortLabel, yRowHeight))
                }
              } 
              if (prevPoint) {
                problemLines.append('path')
                  .attr('d', lineGenerator([
                    [prevPoint.x, prevPoint.y],
                    [sPoint.x, sPoint.y]
                  ])).lower()
                  .attr('pointer-events', 'none')
                  .attr('class', 'prob-' + (prob.problemType.value | 0))
              }
            }
          })
        })
        problemLines.lower()
        // const probsToLink = []
        // this.schools.forEach(school => {
        //   const probs = this.filteredProblems.filter(p => parseInt(p.school.value) === parseInt(school.value))
        //   probsToLink.push(probs)
        // })
        // let lineGenerator = d3.line()

        // let lineGroups = problemGroup.insert('g').lower()
        //   .attr('width', this.viz.width)
        //   .attr('height', this.viz.height)
        //   .attr('x', 0)
        //   .attr('y', 0)
        //   .attr('class', 'schoolLines')
        // probsToLink.forEach(g => {
        //   g.forEach((p, i) => {
        //     if (i === 0) {
        //       return;
        //     } 
        //     const probVert = this.yForStatus(p.status.shortLabel, yRowHeight) + this.yForType(p.problemType.shortLabel, yRowHeight)
        //     const probPoint = {
        //       x: this.xForProblem(p, p.status), 
        //       y: this.viz.height - (this.margins.bottom + probVert)
        //     }
        //     const prevProb = g[i - 1]
        //     const prevProbVert = this.yForStatus(prevProb.status.shortLabel, yRowHeight) + this.yForType(prevProb.problemType.shortLabel, yRowHeight)
        //     const prevPoint = {
        //       x: this.xForProblem(prevProb, prevProb.status), 
        //       y: this.viz.height - (this.margins.bottom + prevProbVert)
        //     }
        //     lineGroups.append('path')
        //       .attr('d', lineGenerator([
        //         [prevPoint.x, prevPoint.y],
        //         [probPoint.x, probPoint.y]
        //       ]))
        //       .lower()
          
        //   })
        // })
      },
      hiliteProb (problem) {
        const yRowHeight = (this.viz.height - this.margins.bottom - this.margins.top) / this.filteredStatuses.length
        const statusVert = this.yForStatus(problem.status.shortLabel, yRowHeight) + this.yForType(problem.problemType.shortLabel, yRowHeight)
        const x = this.xForProblem(problem, problem.status)
        const y = this.viz.height - (this.margins.bottom + statusVert)
        this.highlitedProblem = problem
        this.hideShoolTip()
        this.showProblemTooltip(x, y)
        this.connectProb(problem)
      },
      dehiliteProblem () {
        this.hideTooltip()
        d3.selectAll('.tempProbsDos').remove()
      },
      cancelTooltipHide () {
        clearTimeout(this.hideTimeout)
        this.hideTimeout = null
      },
      showProblemTooltip (x, y) {
        setTimeout(()=> {
          if (!this.highlitedProblem) {
            return
          }
          const tip = d3.select('.tooltip')
          const tipWidth = tip.node().offsetWidth  
          const tipHeight = tip.node().offsetHeight
          const offset = 20
          if (x + offset + tipWidth > this.viz.width && y > tipHeight / 3) {
            tip
            .classed('left', true)
              .attr('style', 'top:' + (y - (tipHeight / 2)) + 'px; left:' + (x - tipWidth - offset) + 'px;')
            return
          }
          if (y < tipHeight) {
            tip
            .classed('top', true)
              .attr('style', 'top:' + (y + offset) + 'px; left:' + (x - (tipWidth / 2)) + 'px;')
            return
          }
          tip
          .classed('right', true)
            .attr('style', 'top:' + (y - (tipHeight / 2)) + 'px; left:' + (x + offset) + 'px;')
        }, 100)
      },
      hideTooltip () {
        d3.select('.tooltip').attr('style', '').classed('top right left shown', false)
        this.highlitedProblem = null
      },
      connectProb (problem) {
        if (this.connectProbs) {
          return
        }
        if (!this.showHistory) {
          return
        }
        const yRowHeight = (this.viz.height - this.margins.bottom - this.margins.top) / this.filteredStatuses.length
        let svg = d3.select('#superChart')
        let tempprob = svg.insert('g')
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('opacity', 1)
          .attr('class', 'tempProbsDos').lower()

        let lineGenerator = d3.line()
        var historyItems = problem.statusHistory.concat([problem.status])
        historyItems = historyItems.filter(s => {
          const found = this.filteredStatuses.findIndex(fs => fs.shortLabel === s.shortLabel)
          return found > -1
        })
        let sPoint
        historyItems.sort((a, b) => {
          return a.lastModified - b.lastModified
        }).forEach((s, i) => {
          const statusVert = this.yForStatus(s.shortLabel, yRowHeight) + this.yForType(problem.problemType.shortLabel, yRowHeight)
          sPoint = { 
            x: this.xForProblem(problem, s), 
            y: this.viz.height - (this.margins.bottom + statusVert)
          }
          let prevPoint
          if (i > 0) {
            const prevStatus = historyItems[i - 1]
            prevPoint = {
              x: this.xForProblem(problem, prevStatus), 
              y: this.viz.height - (this.margins.bottom + this.yForStatus(prevStatus.shortLabel, yRowHeight) + this.yForType(problem.problemType.shortLabel, yRowHeight))
            }
          } 
          if (prevPoint) {
            tempprob.append('path')
              .attr('d', lineGenerator([
                [prevPoint.x, prevPoint.y],
                [sPoint.x, sPoint.y]
              ]))
              .attr('class', 'prob-' + (problem.problemType.value | 0))
              .lower()
          }
        })
      },
      selectProb (problem) {
        this.problemSelected = problem.id
        this.highlitedProblem = problem
        
        d3.selectAll('.tempProbs').remove()
        const yRowHeight = (this.viz.height - this.margins.bottom - this.margins.top) / this.filteredStatuses.length
        let svg = d3.select('#superChart')
        d3.selectAll('#prob-' + problem.id).raise()
        let tempprob = svg.insert('g')
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('opacity', 1)
          .attr('class', 'tempProbs')

        let lineGenerator = d3.line()
        var historyItems = problem.statusHistory.concat([problem.status])
        historyItems = historyItems.filter(s => {
          const found = this.filteredStatuses.findIndex(fs => fs.shortLabel === s.shortLabel)
          return found > -1
        })
        let statusHistoryDates = historyItems.map(s => s.lastModified)
        statusHistoryDates.push(problem.status.lastModified)
        this.shiftProblems('real', statusHistoryDates)
        this.drawTicks(statusHistoryDates)
        let sPoint
        historyItems.sort((a, b) => {
          return a.lastModified - b.lastModified
        }).forEach((s, i) => {
          const statusVert = this.yForStatus(s.shortLabel, yRowHeight) + this.yForType(problem.problemType.shortLabel, yRowHeight)
          sPoint = { 
            x: this.xForProblem(problem, s, statusHistoryDates, 'real'), 
            y: this.viz.height - (this.margins.bottom + statusVert)
          }
          let prevPoint
          if (i > 0) {
            const prevStatus = historyItems[i - 1]
            prevPoint = {
              x: this.xForProblem(problem, prevStatus, statusHistoryDates, 'real'), 
              y: this.viz.height - (this.margins.bottom + this.yForStatus(prevStatus.shortLabel, yRowHeight) + this.yForType(problem.problemType.shortLabel, yRowHeight))
            }
          } 
          if (prevPoint) {
            tempprob.append('path')
              .attr('d', lineGenerator([
                [prevPoint.x, prevPoint.y],
                [sPoint.x, sPoint.y]
              ]))
              .attr('class', 'prob-' + (problem.problemType.value | 0))
              .lower()
          }
          tempprob.append('circle')
            .datum(problem)
            .attr('cx', sPoint.x)
            .attr('cy', sPoint.y)
            .attr('r', this.style.selectedRadius)
            .attr('class', 'tProb prob-' + (problem.problemType.value | 0))
            .attr('stroke', 'white')
            .attr('stroke-width', '2')
            .on('click', (d) => {
              this.editProblem(d) 
            })
        })
        d3.transition()
          .selectAll('prob:not(#prob-' + problem.id + ')')
          .duration(250)
          .ease(d3.easeCubicInOut)
          .attr('opacity', 0.3)
        d3.transition()
          .select('#prob-' + problem.id)
          .duration(250)
          .ease(d3.easeCubicInOut)
          .attr('opacity', 1)
        d3.select('.tooltip').classed('shown', true)
        this.showProblemTooltip(sPoint.x, sPoint.y)
      },
      resetMode (pointsUpdated) {
        d3.transition()
          .selectAll('.prob')
          .duration(150)
          .ease(d3.easeCubicInOut)
          .attr('opacity', 1)
        this.fadeAndRemove('.tempProbs, .axisTicks')
        this.hideTooltip()
        this.problemSelected = undefined
        this.toggleXMode(this.mode, null, pointsUpdated)
      },
      toggleXMode (mode, newPoints, pointsUpdated) {
        if (this.problemSelected !== undefined) {
          this.resetMode()
          return
        }
        if (typeof(mode) === 'string') {
          this.mode = mode
        } else {
          this.mode = (this.mode === 'normalized') ? 'real' : 'normalized'
        }
        if (this.mode === 'normalized') {
          this.showHistory = false
          d3.selectAll('.tProb').remove()
        }
        this.shiftProblems(this.mode, newPoints)
        this.drawTimes(newPoints)
      },
      toggleShowHistory () {
        this.showHistory = !this.showHistory
        this.redraw()
      },
      toggleLineMode () {
        this.connectProbs = !this.connectProbs
        this.redraw()
      },
      shiftProblems (mode, newPoints) {
        const transition = d3.transition('xChange')
          .duration(450)
          .ease(d3.easeCubicInOut)
        transition.selectAll('.prob')
          .attr('x', (d) => this.xForProblem(d, d.status, newPoints, mode) - this.style.radius)
      },
      drawTimes (newDates) {
        let tickDates = []
        const divisions = ((this.viz.width - this.margins.left - this.margins.right) / this.style.tickCount)
        if (this.mode !== 'real') {
          const span = Math.floor(this.filteredProblems.length / this.style.tickCount)
          for (let index = 0; index <= this.style.tickCount; index++) {
            if (this.filteredProblems[index * span]) {
              tickDates.push(this.filteredProblems[index * span].status.lastModified)
            }
          }
        } else {
          const allDates = this.filteredDates.concat(newDates || [])
          if (allDates.length === 0) {
            return
          }
          const minValue = Math.min.apply(null, allDates)
          const maxValue = Math.max.apply(null, allDates)
          const xValues = d3.scaleLinear()
            .domain([0, (this.viz.width - this.margins.left - this.margins.right)])
            .range([minValue, maxValue])

          for (let index = 0; index <= this.style.tickCount; index++) {
            tickDates.push(xValues(divisions * index))
          }
        }
        if (tickDates.length === 0) {
          return
        }
        this.drawTicks(tickDates.sort((a, b) => {
          return a - b
        }), this.mode !== 'real')
      },
      drawTicks (dates, normalized) {
        let xValues = null
        if (normalized) {
          const divisions = ((this.viz.width - this.margins.left - this.margins.right) / this.style.tickCount)
          const span = Math.floor(this.filteredProblems.length / this.style.tickCount)
          xValues = (d, index) => {
            return divisions * index
          }
        } else {
          const allDates = this.filteredDates.concat(dates)
          const minValue = Math.min.apply(null, allDates)
          const maxValue = Math.max.apply(null, allDates)
          xValues = d3.scaleLinear()
            .domain([minValue, maxValue])
            .range([0, (this.viz.width - this.margins.left - this.margins.right)])
        }
        d3.selectAll('.axisTicks').remove()
        let svg = d3.select('#superChart')
        let axisG = svg.insert('g').lower()
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('opacity', 1)
          .attr('class', 'axisTicks')
        let lineGenerator = d3.line()
        let addedDates = []
        dates.forEach((d, i) => {
          const date = new Date(d)
          if (this.mode === 'real') {
            let matchedDate = false
            addedDates.forEach(ad => {
              if (isSameDay(ad, date)) matchedDate = true
            })
            if (matchedDate) {
              return
            }
          }
          addedDates.push(date)
          const x = this.margins.left + xValues(d, i)
          const yPos = this.viz.height - this.margins.bottom + 5
          axisG.append('path')
            .attr('d', lineGenerator([
              [x, this.margins.top],
              [x, yPos]
            ]))
            .attr('opacity', 0.2)
            .lower()
          axisG.append('text')
            .attr('class', 'label')
            .attr('pointer-events', 'none')
            .attr('width', 100)
            .attr('transform', 'translate('+ (x + 8) +','+ (yPos + 15) +'), rotate(270)')
            .style('display', () => { return 'inline' })
            .attr('text-anchor',  'end')
            .text(format(date, 'MM.DD.YY'))
        })

      },
      xForProblem (problem, status, newStatuses, mode) {
        const layoutMode = mode || this.mode
        const spacing = ((this.viz.width - this.margins.left - this.margins.right) / (this.filteredProblems.length + 1))
        const allDates = newStatuses ? this.filteredDates.concat(newStatuses) : this.filteredDates
        if (allDates.length === 0) {
          return 0
        }
        const minValue = Math.min.apply(null, allDates)
        const maxValue = Math.max.apply(null, allDates)
        const xValues = d3.scaleLinear()
          .domain([minValue, maxValue])
          .range([0, (this.viz.width - this.margins.left - this.margins.right)])
        if (layoutMode === 'normalized') {
          return this.margins.left + spacing * problem.normalizedIndex
        }
        return this.margins.left + xValues(status.lastModified)
      },
      yForStatus (statusLabel, yRowHeight) {
        const statusIndex = this.filteredStatuses.findIndex(s => s.shortLabel === statusLabel)
        if (statusIndex != -1) {
          return yRowHeight * statusIndex
        } else {
          return -20
        }
      },
      yForType (problemTypeLabel, yRowHeight) {
        const typeHeight = (yRowHeight / this.types.length)
        const typeIndex = this.types.findIndex(t => t.shortLabel === problemTypeLabel)
        if (typeIndex != -1) {
          return (typeHeight * typeIndex) + (typeHeight / 2)
        } else {
          return 0
        }
      },
      editProblem (problem) {
        this.problemToEdit = JSON.parse(JSON.stringify(problem))
        this.$modal.show('problem-container')
      },
      problemEditorCanceled () {
        this.$modal.hide('problem-container')
        this.edit = false
        this.create = false
        this.problemToEdit = undefined
      },
      problemEditorCreated (createdProblem) {
        this.edit = false
        this.create = false
        this.problemEditorCanceled()
      },
      problemEditorUpdated (updatedProblem) {
        this.edit = false
        this.create = false
        this.problemEditorCanceled()
      },
      redraw (delay) {
        this.resetMode(delay)
        if (delay) {
          // setTimeout(this.doTheRedraw, 400)
        } else {
          this.doTheRedraw()
        }
      },
      doTheRedraw () {
        d3.selectAll('.rows').remove()
        d3.selectAll('.problems').remove()
        d3.selectAll('.axisTicks').remove()
        this.drawRows()
        this.drawProblems()
        // attempt to make remote modifications to problem animations pulse in correct position
        // if (this.focusedSchools) {
        //   d3.selectAll('.focusSchool').remove()
        //   this.focusedSchools.map(schoolValue => this.focusSchool(schoolValue))
        // }
        this.drawTimes()
      },
      toggleTypeHidden (type) {
        const index = this.typesHidden.indexOf(parseInt(type))
        let newTypes = []
        if (index > -1) {
          newTypes = this.typesHidden.filter(t => t != parseInt(type))
        } else {
          newTypes = this.typesHidden.concat([parseInt(type)])
        }
        // Vue.set(this.typesHidden, newTypes)
        this.typesHidden = newTypes
        this.redraw()
      },
      selectAllTypes () {
        this.typesHidden = []
        this.redraw()
      },
      clearAllTypes () {
        this.typesHidden = this.types.map(t => parseInt(t.value))
        this.redraw()
      },
      toggleStatusIndex (index) {
        const flippedIndex = this.statuses.length - (index + 1)
        Vue.set(this.selectedStatusIndexes, flippedIndex, !this.selectedStatusIndexes[flippedIndex])
        this.redraw()
      },
      selectAllStatus () {
        this.statuses.forEach((s, index) => {
          Vue.set(this.selectedStatusIndexes, index, true)
        })
        this.redraw()
      },
      clearAllStatus () {
        this.statuses.forEach((s, index) => {
          Vue.set(this.selectedStatusIndexes, index, false)
        })
        this.redraw()
      },
      clearDates () {
        this.timeRangeStart = ''
        this.timeRangeEnd = ''
        this.redraw()
      },
      rangeDateChanged () {
        this.redraw()
      },
      showOptions (option) {
        this.$modal.show(option)
      },
      hideOptions (option) {
        this.$modal.hide(option)
      },
      parseDate (dateString, formatString) {
        return parse(dateString)
      },
      formatDate (dateObj, formatString) {
        return format(dateObj, formatString)
      },
      isFutureDate (date) {
        let startCheck = false
        if (this.timeRangeStart !== '') {
          startCheck = date < parse(this.timeRangeStart)
        }
        const currentDate = new Date()
        return startCheck || date > currentDate
      },
      isFutureStartDate (date) {
        if (this.timeRangeEnd === '') {
          return false
        }
        return date > parse(this.timeRangeEnd)
      },
      problemAddedRemotely (problem) {
        // log('➕ %s %s', problem.status.value, problem.status.lastModifiedDate)
      },
      problemModifiedRemotely (problem) {
        // log('💋 role: %s %s %s', this.profile.role, problem.status.value, problem.status.lastModifiedDate, problem)
        const localProblemIndex = this.problems.findIndex(p => p.id === problem.id)
        if (localProblemIndex > -1) {
          Vue.set(this.problems, localProblemIndex, problem)
          this.redraw(true)
          d3.selectAll('#prob-' + problem.id).datum(problem)
          setTimeout(() => {
            this.pulseProblem(problem)
          }, 500)
        }
      },
      problemRemovedRemotely (problemId) {
        // log('🗑 %s', problemId)
      },
      pulseProblem (problem) {
        const prob = d3.select('#prob-' + problem.id)
        prob.raise()
        const svg = d3.select('#superChart')
        let pulseGroup = svg.insert('g').raise()
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('class', 'pulse')
        let p = pulseGroup.append('circle')
          .datum(problem)
          .attr('cx', parseFloat(prob.attr('x')))
          .attr('cy', parseFloat(prob.attr('y')))
          .attr('r', this.style.radius)
          .attr('opacity', 1)
          .attr('class', 'tProb prob-' + (problem.problemType.value | 0))
        pulseGroup.append('circle')
          .datum(problem)
          .attr('cx', parseFloat(prob.attr('x')))
          .attr('cy', parseFloat(prob.attr('y')))
          .attr('r', this.style.radius)
          .attr('class', 'tProb prob-' + (problem.problemType.value | 0))
        this.pulse(p, 0)
      },
      pulse (node, count) {
        if (count === 3) {
          d3.selectAll('.pulse').remove()
          return
        }
        count += 1
        node.transition('xxxx')
          .duration(2000)
          .ease(d3.easeCubicInOut)
          .attr('opacity', 0)
          .attr('r', 20)
          .on('end', () => {
            node
              .attr('opacity', 1)
              .attr('r', this.style.radius)
            this.pulse(node, count)
          })
      },
      fadeAndRemove (selector) {
        d3.transition('remove')
          .selectAll(selector)
          .duration(350)
          .ease(d3.easeCubicInOut)
          .attr('opacity', 0)
          .remove()
      },
      composeDate (date) {
        var dateText = format(date, 'MMM DD')
        return dateText
      },
      problemCountForSchool (school) {
        return this.filteredProblems.filter(p => parseInt(p.school.value) === school).length
      },
      pctCompleteForSchool (school) {
        const base = this.problems.filter(p => parseInt(p.school.value) === school)
        const values = [8, 9]
        const completed = base.filter(p => values.indexOf(parseInt(p.status.value)) > -1)
        const pct = Math.floor(completed.length / base.length * 100)
        return pct > 0 ? pct + '%' : ''
      },
      adtcForSchool (school) {
        const base = this.filteredProblems.filter(p => parseInt(p.school.value) === school)
        return this.adtc(base)
      },
      adtc (base) {
        if (!this.filteredProblems) {
          return 0
        }
        const value = 8
        const resolved = (base || this.filteredProblems).filter(p => {
          if (parseInt(p.status.value) === value) {
            return true
          } else {
            return p.statusHistory.filter(s => parseInt(s.value) === value).length > 0
          }
          return false
        })
        if (resolved.length === 0) {
          return ''
        }
        const daysToCompleted = resolved.map(p => {
          const reportedDate = p.statusHistory.filter(s => parseInt(s.value) === 0)[0].lastModified
          let completedDate
          if (parseInt(p.status.value) === value) {
            completedDate = p.status.lastModified
          } else {
            completedDate = p.statusHistory.filter(s => parseInt(s.value) === value)[0].lastModified
          }
          return differenceInDays(completedDate, reportedDate)
        })
        const avrg = Math.round(daysToCompleted.reduce((p, c) => p + c, 0) / daysToCompleted.length)
        return avrg
      },
      dtcForProblem (problem) {
        const completedStatusValues = [8,9]
        if (completedStatusValues.indexOf(parseInt(problem.status.value)) > -1) {
          return differenceInDays(problem.status.lastModified, problem.created)
        }
        return ''
      },
      /**
       * @return days since last update on the problem
       */
      dtluForProblem (problem) {
        const now = new Date().getTime()
        if (problem.status.lastModified) {
          return differenceInDays(now, problem.status.lastModified)
        } else {
          return ''
        }
      },
      focusedSchoolsProblems(schoolValue) {
        if (!this.focusedSchools) {
          return []
        }
        return this.filteredProblems.filter(p => parseInt(p.school.value) === parseInt(schoolValue))
      }
    }
  }
</script>

<style lang="scss">
  .chart-view {
    padding-top: 65px;
    position: relative;
    .header-toolbar {
      display: flex;
      justify-content: space-between;
      padding: 0 0 0 140px;
      height: 30px;
      .right {
        display: flex;
        justify-content: space-between;
      }
      .btn {
        margin-right: 10px;
      }
      .field {
        display: inline-block;
        margin-left: 10px;
        padding-top: 2px;
        .field {
          padding-top: 0;
        }
      }
      div.field {
        margin-left: 20px;
      }
      .toolbar {
        position: static;
        width: 280px;
        height: 40px;
        z-index: 2;
        padding:0 20px 0;
        &::before {
          content: ' ';
          display: block;
          position: relative;
        }
        .btn {
          margin: 0 0 0 10px;
        }
        .input {
          width:131px;
          display: inline-block;
          position: relative;
          .clear {
            display: inline-block;
            width: 15px;
            height: 15px;
            background-color: #ccc;
            position: absolute;
            top: 4px;
            right: 8px;
            font-size: 0.9rem;
            font-weight: bold;
            line-height: 0.9;
            text-align: center;
            border-radius: 50%;
            color: white;
            cursor: pointer;
            &:hover {
              background-color: #aaa;
            }
          }
          input {
            width:117px;
            padding: 3px;
          }
        }
      }
    }
  }
  .datepickers {
    overflow: visible !important;
  }
  .status-options {
    padding: 20px;
    position: relative;
    .toolbar {
      position: fixed;
      background: white;
      height: 40px;
      margin-top:-20px;
      z-index: 2;
      width: 360px;
      padding:20px 0 0;
      .btn-cancel {
        float: right;
      }
      .btn {
        margin-top: 5px;
        margin-right: 10px;
      }
    }
    ul {
      list-style: none;
      margin: 0;
      padding: 40px 0 0;
      li {
        position: relative;
        margin-bottom: 1px;
        font-weight: bold;
        color: white;
        text-shadow: 0 0 4px rgba(0,0,0,0.7);
        cursor: pointer;
        &:hover {
          opacity: 0.8;
        }
        div {
          padding: 10px 15px 10px 40px;
        }
        .check {
          position: absolute;
          width: 15px;
          height: 15px;
          left: 14px;
          top:8px;
          font-weight: normal;
          font-size: 1.0rem;
        }
      }
    }
    &.schools {
      padding: 20px;
      ul {
        padding-top: 0;
        li {
          color: #242424;
          text-shadow: none;
          margin-bottom: 0;
          background-color: rgba(0,0,0,0.05);
          cursor: default;
          &.focused {
            opacity: 1;
          }
          div.schoolinfo {
            font-weight: normal;
            font-size: 0.70rem;
            padding: 0;
            border-bottom: 1px solid white;
            display: grid;
            grid-template-columns: 30px 1fr;
            align-content: center;
            .details {
              display: grid;
              grid-template-columns: 1fr 40px 40px 40px;
              align-content: center;
              cursor: pointer;
            }
            .input {
              padding: 5px 0 5px 5px;
              display: grid;
              align-content: center;
              border-right: 1px solid white;
            }
            .info {
              padding: 6px 0 6px 5px;
            }
            .count {
              text-align: center;
              border-left: 1px solid white;
              display: grid;
              align-content: center;
            }
            input {
              margin-right: 5px;
            }
          }
          .problemsList {
            padding: 0;
            border-bottom: 1px solid white;
            .item {
              padding: 0;
              font-weight: normal;
              &:not(:last-child) {
                border-bottom: 1px solid #ccc;
              }
              &.hilited {
                background: rgba(0,0,0,0.1);
              }
              padding: 0;
              display: grid;
              grid-template-columns: 1fr 40px;
              align-content: center;
              .count {
                padding: 0;
                text-align: center;
                border-left: 1px solid white;
                display: grid;
                align-content: center;
                font-size: 0.7rem;
              }
              .status {
                font-size: 0.7rem;
                padding: 5px 9px;
                .status-flag {
                  border-radius: 1px;
                  width: 12px;
                  height: 12px;
                  margin-right: 5px;
                  display: inline-block;
                }
              }
            }
          }
        }
      }
    }
    .dateRange {
      padding-top: 40px;
      display: flex;
      label {
        display: block;
        margin-bottom: 5px;
      }
      .field {
        margin-right: 10px;
      }
    }
  }
  .container {
    position: relative;
    display: grid;
    grid-template-columns: 1fr 300px;
    grid-template-rows: auto;
    grid-column-gap: 0px;
    grid-row-gap: 0px;
    .sidebar {
      display: grid;
      grid-template-rows: 1fr auto auto auto;

      .status-options {
        position: relative;
        overflow: scroll;
        margin-top: 10px;
        padding: 0 20px 0 0;
      }
      .schoolTotals {
        padding: 9px 10px;
        font-size: 0.7rem;
        margin-right: 20px;
        display: flex;
        justify-content: space-between;
        .info {
        }
        .count {
        }
      }
    }
  }
  .chart {
    position: relative;
  }
  .tip {
    position: absolute;
    top: 0;
    left: -999px;
    z-index: 10;
    background: #eee;
    padding:8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    h4 {
      margin: 0;
    }
    &.shown {
      transition: all 0.3s ease-in-out;
    }
    &::before {
      position: absolute;
      content: ' ';
      display: block;
      width: 0;
      height: 0;
      border-style: solid;
    }
    &.right::before {
      left: -10px;
      top: 50%;
      margin-top:-9px;
      border-width: 8px 10px 8px 0;
      border-color: transparent #eee transparent transparent;
    }
    &.left::before {
      right: -10px;
      top: 50%;
      margin-top:-9px;
      border-width: 8px 0 8px 10px;
      border-color: transparent transparent transparent #eee;
    }
    &.top::before {
      top: -10px;
      left: 50%;
      margin-left:-9px;
      border-width: 0 8px 10px 8px;
      border-color: transparent transparent #eee transparent;
    }
    &.bottom::before {
      bottom: -10px;
      left: 50%;
      margin-left:-9px;
      border-width: 10px 8px 0 8px;
      border-color: #eee transparent  transparent;
    }
    .problem {
      width: 300px;
      .inner {
        background: white;
      }
      .inner:hover {
        background: white;
        cursor: default;
      }
      .images img {
        cursor: default;
      } 
      button {
        display: none;
      }
    }

    .c3-tooltip {
      box-shadow: none;
    }
  }
  #superChart {
    .prob,
    .tProb,
    .statusfield {
      cursor: pointer;
    }
    text {
     font-size: 0.6rem;
     font-weight: 500;
    }
    path {
      stroke: #666;
    }
    foreignObject {
      overflow: visible;
      font-size:0.5rem;
      font-weight: bold;
      body {
        background: transparent !important;
        overflow: visible;
        display: grid;
        align-content: center;
        .label_el {
          text-align: right;
        }
      }
    }
    .prob-18 {
      fill: rgb(31, 119, 180);
    }
    .prob-12 {
      fill: rgb(255, 127, 14);
    }
    .prob-20 {
      fill: rgb(44, 160, 44);
    }
    .prob-14 {
      fill: rgb(214, 39, 40);
    }
    .prob-15 {
      fill: rgb(148, 103, 189);
    }
    .prob-16 {
      fill: rgb(140, 86, 75);
    }
    .prob-17 {
      fill: rgb(227, 119, 194);
    }
    .prob-19 {
      fill: rgb(127, 127, 127);
    }
    .prob-13 {
      fill: rgb(188, 189, 34);
    }
    .prob-21 {
      fill: rgb(23, 190, 207);
    }
    .prob-22 {
      fill: rgb(0, 94, 152);
    }
    .prob-23 {
      fill: rgb(217, 95, 0);
    }
    .prob-24 {
      fill: rgb(0, 141, 37);
    }

    path.prob-18 {
      fill: rgb(31, 119, 180);
      stroke: rgb(31, 119, 180);
    }
    path.prob-12 {
      fill: rgb(255, 127, 14);
      stroke: rgb(255, 127, 14);
    }
    path.prob-20 {
      fill: rgb(44, 160, 44);
      stroke: rgb(44, 160, 44);
    }
    path.prob-14 {
      fill: rgb(214, 39, 40);
      stroke: rgb(214, 39, 40);
    }
    path.prob-15 {
      fill: rgb(148, 103, 189);
      stroke: rgb(148, 103, 189);
    }
    path.prob-16 {
      fill: rgb(140, 86, 75);
      stroke: rgb(140, 86, 75);
    }
    path.prob-17 {
      fill: rgb(227, 119, 194);
      stroke: rgb(227, 119, 194);
    }
    path.prob-19 {
      fill: rgb(127, 127, 127);
      stroke: rgb(127, 127, 127);
    }
    path.prob-13 {
      fill: rgb(188, 189, 34);
      stroke: rgb(188, 189, 34);
    }
    path.prob-21 {
      fill: rgb(23, 190, 207);
      stroke: rgb(23, 190, 207);
    }
    path.prob-22 {
      fill: rgb(0, 94, 152);
      stroke: rgb(0, 94, 152);
    }
    path.prob-23 {
      fill: rgb(217, 95, 0);
      stroke: rgb(217, 95, 0);
    }
    path.prob-24 {
      fill: rgb(0, 141, 37);
      stroke: rgb(0, 141, 37);
    }
    .status--1 {
      fill:rgb(127, 127, 127);
      stroke:rgb(127, 127, 127);
    }
    .status-0 {
      fill: hsla(17, 99%, 65%, 1);
      stroke: hsla(17, 99%, 65%, 1);
    }
    .status-1 {
      fill: hsla(29, 100%, 66%, 1);
      stroke: hsla(29, 100%, 66%, 1);
    }
    .status-2 {
      fill: hsla(43, 100%, 66%, 1);
      stroke: hsla(43, 100%, 66%, 1);
    }
    .status-3 {
      fill: #7bc67b;
      stroke: #7bc67b;
    }
    .status-4 {
      fill: #009345;
      stroke: #009345;
    }
    .status-5 {
      fill: hsla(177, 88%, 35%, 1);
      stroke: hsla(177, 88%, 35%, 1);
    }
    .status-6 {
      fill: hsla(207, 82%, 42%, 1);
      stroke: hsla(207, 82%, 42%, 1);
    }
    .status-7 {
      fill: hsla(229, 66%, 37%, 1);
      stroke: hsla(229, 66%, 37%, 1);
    }
    .status-8 {
      fill: #752287;
      stroke: #752287;
    }
    .status-9 {
      fill: hsla(320, 56%, 48%, 1);
      stroke: hsla(320, 56%, 48%, 1);
    }
    .status-10 {
      fill: #abd579;
      stroke: #abd579;
    }
    .status-14 {
      fill: #38b97c;
      stroke: #38b97c;
    }
    .status-11 {
      fill: #34b653;
      stroke: #34b653;
    }
    .status-12 {
      fill: #00a757;
      stroke: #00a757;
    }
    .status-13 {
      fill: #542287;
      stroke: #542287;
    }
    .status-15 {
      fill:#d37028;
      stroke:#d37028;
    }
  }

</style>